import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';

import { getQuery } from 'client/utils/location';
import { NATIONAL_LEASE_DEALS_PAGE_URL } from 'site-modules/lease-deals/constants/lease-deals-constants';
import {
  ZERO_DOWN_PARAM,
  DEALS_FILTER_VALUES,
  FILTER_QUERY_PARAMS,
} from 'site-modules/shared/constants/lease-deals/lease-deals';
import gemsIcon from 'client/images/shop-4931/gems.svg';
import allIcon from 'client/images/shop-4931/all.svg';
import zeroDownIcon from 'client/images/shop-4931/0-down.svg';
import under299Icon from 'client/images/shop-4931/under-299.svg';
import sedanIcon from 'client/images/shop-4931/sedan.svg';
import suvIcon from 'client/images/shop-4931/suv.svg';
import luxuryIcon from 'client/images/shop-4931/luxury.svg';

import './lease-deals-icon-nav.scss';

const ICON_NAV_ITEMS = [
  {
    label: 'All',
    href: NATIONAL_LEASE_DEALS_PAGE_URL,
    active: (pathname, query) =>
      pathname === NATIONAL_LEASE_DEALS_PAGE_URL && !query?.deals && !query?.[ZERO_DOWN_PARAM],
    icon: allIcon,
  },
  {
    label: 'Gems',
    href: `${NATIONAL_LEASE_DEALS_PAGE_URL}?${FILTER_QUERY_PARAMS.DEALS}=${DEALS_FILTER_VALUES.GEMS}`,
    active: (pathname, query) =>
      pathname === NATIONAL_LEASE_DEALS_PAGE_URL && query?.deals === DEALS_FILTER_VALUES.GEMS,
    icon: gemsIcon,
  },
  {
    label: '$0 Down',
    href: `${NATIONAL_LEASE_DEALS_PAGE_URL}?${ZERO_DOWN_PARAM}=true`,
    active: (_, query) => query?.[ZERO_DOWN_PARAM] === 'true',
    icon: zeroDownIcon,
  },
  {
    label: 'Under $299',
    href: '/car-leasing/monthly-199-lease-deals.html',
    active: pathname => pathname === '/car-leasing/monthly-199-lease-deals.html',
    icon: under299Icon,
  },
  {
    label: 'Sedan',
    href: '/best-sedan-lease-deals/',
    active: pathname => pathname === '/best-sedan-lease-deals/',
    icon: sedanIcon,
  },
  {
    label: 'SUV',
    href: '/best-suv-lease-deals/',
    active: pathname => pathname === '/best-suv-lease-deals/',
    icon: suvIcon,
  },
  {
    label: 'Luxury',
    href: '/best-luxury-lease-deals/',
    active: pathname => pathname === '/best-luxury-lease-deals/',
    icon: luxuryIcon,
  },
];

export function LeaseDealsIconNav({ withPreviewCard = false }) {
  const location = useSelector(state => state.pageContext?.location);
  const { pathname = '' } = location;
  const query = getQuery(location);

  const { activeNavItem, navItems } = ICON_NAV_ITEMS.reduce(
    (acc, item) => {
      const isActive = item.active(pathname, query);
      if (isActive) {
        acc.activeNavItem = item;
      } else {
        acc.navItems.push(item);
      }

      return acc;
    },
    { activeNavItem: null, navItems: [] }
  );

  return (
    <nav
      aria-label="Lease deals navigation"
      className={classnames('lease-deals-icon-nav pt-2 pt-md-1 pb-0', {
        'pb-md-1_5': !withPreviewCard,
        'pb-md-0_5': withPreviewCard,
      })}
      data-tracking-parent="edm-lease-deals-icon-nav"
    >
      <Row
        className="lease-deals-icon-nav-items d-flex gap-1 flex-nowrap mx-0 justify-content-start justify-content-md-center list-unstyled px-0_5 my-0"
        tag="ul"
      >
        {[activeNavItem, ...navItems].filter(Boolean).map(({ label, href, active, icon }) => {
          const isActive = active(pathname, query);
          return (
            <li key={label} className="m-0 p-0">
              <a
                href={href}
                className={classnames(
                  'lease-deals-icon-nav-item d-flex flex-column justify-content-between align-items-center size-14 text-decoration-none fw-medium px-0_5 py-0_75 flex-shrink-0 rounded-8 text-nowrap',
                  { active: isActive }
                )}
                aria-current={isActive ? 'page' : undefined}
                data-tracking-id="lease_deals_icon_nav_link"
                data-tracking-value={label}
              >
                <span className="d-flex mb-0_5 mx-auto justify-content-center">
                  <img src={icon} alt="" aria-hidden />
                </span>
                {label}
              </a>
            </li>
          );
        })}
      </Row>
    </nav>
  );
}

LeaseDealsIconNav.propTypes = {
  withPreviewCard: PropTypes.bool,
};
